import HRTitleHeader from "src/components/ui/HRTitleHeader";
import LeaveTableRow from "./components/LeaveTableRow";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Filters, useLeaves } from "./hooks/useLeaves";
import { CustomerListPagination } from "../../ManageCustomers/components/customer-pagination";
import Search from "src/components/input/Search";
import { updateSearchParams } from "src/utils";

const pagesize = 10;

const LeaveManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get("page") || "1", 10);
  const name = searchParams.get("name") || "";
  const [failSafePageNumber, setFailSafePageNumber] = useState<number>(1);

  const filter: Filters = {
    page,
    pagesize,
    name
  }

  const { data: leaves = [], isLoading, isFetching } = useLeaves(filter);

  const hasNextPage = leaves?.length === pagesize;
  const hasPrevPage = page > 1;

  useEffect(() => {
    if (!leaves) return;
    if (leaves?.length > 0) {
      setFailSafePageNumber(page);
    } else if (failSafePageNumber !== page) {
      toast.info("No more Leaves!");
      setSearchParams({
        ...Object.fromEntries(searchParams),
        page: failSafePageNumber.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaves]);

  const handleNextPage = () => {
    setFailSafePageNumber(page);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page + 1).toString(),
    });
  };

  const handlePrevPage = () => {
    if (page === 1) {
      toast.info("This is the first page");
      return;
    }
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page - 1).toString(),
    });
  };

  const handleJumpToPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });
  };

  return (
    <div className="pt-6">
      <div className="sticky top-0 backdrop-blur-lg">
        <HRTitleHeader
          title="Leave Management"
          info="Approve or review leave requests"
        />

        <div className="mb-2">
          <Search
            value={name}
            placeholder="Search by Name"
            onChange={(value) =>
              updateSearchParams(
                "name",
                value,
                setSearchParams,
                searchParams,
              )
            }
            className="max-w-xl"
          />
        </div>
      </div>

      {isLoading || leaves.length === 0 ? (
        <div className="text-center text-gray-500 mt-10 flex items-center justify-center gap-2 w-full">
          <h2>Loading Leave Requests...</h2>
          <span className="loading loading-dots loading-md"></span>
        </div>
      ) : (
        <div>
          <div className="shadow-lg rounded-2xl bg-card max-h-[50ram] overflow-y-auto">
            <div className="p-0">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-left text-slate-500 font-medium text-sm">
                    <th className="p-4">Name</th>
                    <th className="p-4">Designation</th>
                    <th className="p-4">Requested On</th>
                    <th className="p-4">From</th>
                    <th className="p-4">To</th>
                    <th className="p-4">Type/Days</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(leaves) &&
                    leaves.length > 0 &&
                    leaves.map((leave) => (
                      <LeaveTableRow leave={leave} key={leave.id} />
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex-grow my-3">
            <CustomerListPagination
              currentPageNumber={page}
              onNext={handleNextPage}
              onPrev={handlePrevPage}
              onJumpToPage={handleJumpToPage}
              isFetching={isFetching}
              hasPrev={hasPrevPage}
              hasNext={hasNextPage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;
